import React, { useState, useEffect } from "react";
import LawyerAdvancedSearch from "../../components/lawyers/LawyerAdvancedSearch";
import LawyerCard from "../../components/lawyers/LawyerCard";
import LawyerList from "../../components/lawyers/LawyerList";
import LawyersPagination from "../../components/lawyers/LawyersPagination";
import LawyerLevelFilter from "../../components/lawyers/LawyerLevelFilter";
import GovernorateLinks from "../../components/lawyers/GovernorateLinks";
import PageContent from "../../components/lawyers/PageContent";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import NotFound from "../NotFound/NotFound";
import { useLawyerContext } from "../../shared/context/LawyerContext";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Heading from "../../components/lawyers/Heading";
import { useTranslation } from "react-i18next";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import useCanonical from "../../shared/seo/useCanonical";

const PAGE_SIZE = 30;

/**
 * /محامون, /Avocats/sfax, /Lawyers/sfax/cassation?page=2 …
 * The city and level segments may be in any spelling (old links used
 * "Mannouba", "Ben_Arous", Arabic names); the canonical one replaces them.
 */
export default function Lawyers() {
  const { t } = useTranslation();
  const lang = currentLang();
  const { getLawyersDirectory } = useLawyerContext();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [lawyerList, setLawyerList] = useState([]);
  const [total, setTotal] = useState(0);

  // Old links and searches without a city may carry "undefined" as the state.
  const stateParam = params.state && params.state !== "undefined" ? params.state : null;
  const governorate = stateParam ? urls.findGovernorate(stateParam) : null;
  const barLevel = params.level ? urls.findBarLevel(params.level) : null;
  const unknown = (stateParam && !governorate) || (params.level && !barLevel);
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  // The name search is not part of the URL (not indexable); see LawyerAdvancedSearch.
  const name = (location.state && location.state.name) || "";

  const canonical = unknown ? null : urls.lawyersPath(lang, { governorate, barLevel, page });
  useCanonical(canonical);

  const govKey = governorate ? governorate.key : null;
  const levelKey = barLevel ? barLevel.key : null;
  useEffect(() => {
    if (unknown) return undefined;
    let cancelled = false;
    async function fetchLawyers() {
      setIsLoading(true);
      const res = await getLawyersDirectory({
        governorate: govKey || undefined,
        barLevel: levelKey || undefined,
        name: name || undefined,
        page: page,
        pageSize: PAGE_SIZE,
      });
      if (cancelled) return;
      setLawyerList(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
      setIsLoading(false);
    }
    fetchLawyers();
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
    // getLawyersDirectory is left out: the provider recreates it on every
    // render, which would refetch in a loop.
  }, [govKey, levelKey, name, page, unknown]);

  if (unknown) return <NotFound />;

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const text = copy.lawyers(lang, { governorate, barLevel, total, page });
  const pathFor = (p) => urls.lawyersPath(lang, { governorate, barLevel, page: p });
  // An empty city x level page is a 404 for crawlers; show it as such here.
  const emptyLevel = !isLoading && !name && barLevel && total === 0;

  return (
    <>
      <Seo
        title={text.title}
        description={text.description}
        path={canonical}
        noindex={Boolean(emptyLevel) || (!isLoading && page > pages)}
        alternates={urls.alternates((l) => urls.lawyersPath(l, { governorate, barLevel, page }))}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { name: t("Home"), path: urls.homePath(lang) },
            { name: t("Lawyers"), path: urls.lawyersPath(lang) },
            governorate && { name: urls.governorateName(governorate, lang), path: urls.lawyersPath(lang, { governorate }) },
            governorate && barLevel && { name: text.h1, path: urls.lawyersPath(lang, { governorate, barLevel }) },
          ]
            .filter(Boolean)
            .map((item, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: item.name,
              item: urls.absoluteUrl(window.location.origin, item.path),
            })),
        }}
      />
      <Heading title={text.h1} lead={text.intro}>
        <LawyerLevelFilter lang={lang} governorate={governorate} barLevel={barLevel} />
      </Heading>

      <PageContent>
        <LawyerAdvancedSearch governorate={governorate} barLevel={barLevel} name={name} />
        <LawyerList>
          {isLoading ? (
            <ComponentsLoader />
          ) : lawyerList.length !== 0 ? (
            lawyerList.map((lawyer) => (
              <LawyerCard key={lawyer.id || lawyer._id} lawyer={lawyer} />
            ))
          ) : (
            <div className="avc-empty">
              <span className="avc-empty__icon">
                <i className="fas fa-search" aria-hidden="true"></i>
              </span>
              <h2 className="avc-empty__title">{t("No_lawyer_found")}</h2>
              <p className="avc-empty__text">
                {emptyLevel ? t("lawyers_empty_level") : t("lawyers_change_search")}
              </p>
            </div>
          )}
          {!isLoading && (
            <LawyersPagination
              page={page}
              pages={pages}
              pathFor={pathFor}
              state={name ? { name } : undefined}
            />
          )}
          <GovernorateLinks lang={lang} current={governorate} />
        </LawyerList>
      </PageContent>
    </>
  );
}
